// ABOUTME: Relation news management view with message cards and a create button.
// ABOUTME: Drafts reopen the editor; published messages open the read-only viewer with unpublish.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { showToastMessage, showToastMessageRequestError } from "../../../../../utils/toasts";
import { tokens } from "../../../../../theme";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import MCreateRelationNews from "../../../../../components/global/Modals/MCreateRelationNews/MCreateRelationNews";
import MViewRelationNews from "../../../../../components/global/Modals/MCreateRelationNews/MViewRelationNews";
import {
    useGetRelationNewsListQuery,
    useUnpublishRelationNewsMutation,
    useDeleteRelationNewsMutation,
} from "../../../../../newapi/warehouse/relationNewsSlice";

const formatDate = (raw) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
};

// "All relations", or the first few targeted names with a "+N" tail.
const MAX_AUDIENCE_NAMES = 3;
const formatAudience = (relations, t) => {
    if (!relations || relations.length === 0) return t("all_relations");
    const names = relations.slice(0, MAX_AUDIENCE_NAMES).map((r) => r.name).join(", ");
    const rest = relations.length - MAX_AUDIENCE_NAMES;
    return rest > 0 ? `${names} +${rest}` : names;
};

const All = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const { data: messages = [] } = useGetRelationNewsListQuery();
    const [unpublishRelationNews] = useUnpublishRelationNewsMutation();
    const [deleteRelationNews] = useDeleteRelationNewsMutation();

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });

        return () => {
        }
    }, []);

    const selected = messages.find((m) => m.id === selectedId) ?? null;

    const handleUnpublish = async (newsItem) => {
        try {
            await unpublishRelationNews(newsItem.id).unwrap();
            showToastMessage("success", t("melding_unpublished") || "Gedepubliceerd");
            setSelectedId(null);
            setViewModalOpen(false);
        } catch (err) {
            showToastMessageRequestError(t("unpublish_failed") || "Depubliceren mislukt", t, err);
        }
    };

    const handleDelete = async (newsId) => {
        try {
            await deleteRelationNews(newsId).unwrap();
            showToastMessage("success", t("deleted_successfully") || "Verwijderd");
            setSelectedId(null);
            setCreateModalOpen(false);
        } catch (err) {
            showToastMessageRequestError(t("delete_failed") || "Verwijderen mislukt", t, err);
        }
    };

    const handleCardClick = (message) => {
        setSelectedId(message.id);
        if (message.state === "published") {
            setViewModalOpen(true);
        } else {
            setCreateModalOpen(true);
        }
    };

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Subheading>{t("news")}</Subheading>
                </Box>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        onClick={() => { setSelectedId(null); setCreateModalOpen(true); }}
                        endIcon={<AddIcon />}
                        variant="contained"
                    >
                        {t("new_news_message") || "Nieuw bericht"}
                    </Shbutton>
                </ButtonGroup>
            </Box>

            {messages.length === 0 ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                    <Text light>{t("no_news_messages") || "Nog geen nieuwsberichten"}</Text>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 2,
                    }}
                >
                    {messages.map((message) => {
                        const isPublished = message.state === "published";
                        return (
                            <Box
                                key={message.id}
                                onClick={() => handleCardClick(message)}
                                sx={{
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    p: 2,
                                    background: theme.palette.mode === "dark" ? colors.grey[300] : "#fff",
                                    boxShadow: `0px 1px 2px 1px rgba(49, 65, 95, 0.15)`,
                                    transition: "150ms",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                    "&:hover": {
                                        boxShadow: `0px 4px 12px rgba(49, 65, 95, 0.2)`,
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1.5,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            fontFamily: "Roboto, sans-serif",
                                            background: isPublished
                                                ? colors.green[100]
                                                : theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                            color: isPublished
                                                ? colors.green[400]
                                                : colors.txt["secondary"],
                                        }}
                                    >
                                        {isPublished ? t("published") || "Gepubliceerd" : t("draft") || "Concept"}
                                    </Box>
                                </Box>
                                <Text bold fontSize={15}>
                                    {message.title || t("untitled") || "Zonder titel"}
                                </Text>
                                <Text light fontSize={12}>
                                    {formatDate(message.publishedAt || message.createdAt)}
                                </Text>
                                <Text light fontSize={12}>
                                    {(message.warehouses ?? []).map((w) => w.name).join(", ")}
                                </Text>
                                <Text light fontSize={12}>
                                    {formatAudience(message.relations, t)}
                                </Text>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <MCreateRelationNews
                open={createModalOpen}
                handleClose={() => { setSelectedId(null); setCreateModalOpen(false); }}
                newsId={selectedId}
                onDelete={handleDelete}
            />

            <MViewRelationNews
                open={viewModalOpen}
                handleClose={() => { setSelectedId(null); setViewModalOpen(false); }}
                newsItem={selected}
                logoUrl={null}
                onUnpublish={handleUnpublish}
            />
        </Box>
    );
};

export default All;
